import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { searchProducts, money } from "../lib/api";

export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults([]);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      searchProducts(q.trim())
        .then((d) => setResults(d.items.filter((p) => p.in_stock && (p.price > 0 || p.from_price > 0))))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-brand-ink/60 backdrop-blur-sm" onClick={onClose} data-testid="search-overlay">
      <div
        className="mx-auto mt-0 w-full max-w-2xl bg-white shadow-[0_16px_48px_rgba(0,0,0,0.2)] sm:mt-16"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-neutral-200 p-4">
          <Search className="h-5 w-5 text-neutral-400" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search flower, edibles, vapes..."
            className="w-full bg-transparent text-base outline-none placeholder:text-neutral-400"
            aria-label="Search products"
            data-testid="search-input"
          />
          <button onClick={onClose} aria-label="Close search" className="rounded-full p-1.5 hover:bg-neutral-100" data-testid="search-close-button">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto" data-testid="search-results">
          {loading && <p className="p-5 text-sm text-neutral-500">Searching...</p>}
          {!loading && q && results.length === 0 && (
            <p className="p-5 text-sm text-neutral-500" data-testid="search-no-results">No products found for "{q}".</p>
          )}
          {results.map((p) => (
            <Link
              key={p.product_id}
              to={`/product/${p.slug}`}
              onClick={onClose}
              data-testid={`search-result-${p.slug}`}
              className="flex items-center gap-4 border-b border-neutral-100 p-4 transition-colors hover:bg-brand-sand"
            >
              <div className="h-14 w-14 flex-shrink-0 overflow-hidden bg-brand-sand">
                {p.image && <img src={p.image} alt={p.name} className="h-full w-full object-cover" loading="lazy" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{p.name}</p>
                <p className="text-xs text-neutral-500">{p.in_stock ? "In stock" : "Sold out"}</p>
              </div>
              <p className="text-sm font-bold">{money(p.price)}</p>
            </Link>
          ))}
          {!q && <p className="p-5 text-sm text-neutral-500">Start typing to search the full menu.</p>}
        </div>
      </div>
    </div>
  );
}
